'use client'

import {
  SizeRadiusS,
  SizeSpacing1,
  SizeSpacing20,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Loader } from '@snowball-tech/fractal/dist/src/components/Loader'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import Image from 'next/image'

import type {
  ComponentProps,
  Dispatch,
  SetStateAction,
  SyntheticEvent,
} from 'react'
import { createPortal } from 'react-dom'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNumber from 'lodash/fp/isNumber'
import omit from 'lodash/fp/omit'

import type { ImageSize, Translator } from '../../types'

import { ObjectFits } from '../../enums'
import { useOnPageKeyDown } from '../../hooks/useOnPageKeyDown'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import { asPx, spacings } from '../../styles/helpers'
import DisplayError from '../DisplayError'
import LegacyTable from '../LegacyTable'

type Props = {
  url: string
  alt?: string
  asImg?: boolean
  exitZoom?: () => void
  id?: string
  inlineStyle?: boolean
  isLoading?: boolean
  maxWidth?: number | string
  objectFit?: ObjectFits
  padding?: 'none' | 'small'
  setIsLoading?: Dispatch<SetStateAction<boolean>>
  size?: ImageSize
  withLink?: boolean
  withLoader?: boolean
  zoomed?: boolean
} & Omit<
  ComponentProps<typeof Image>,
  'alt' | 'id' | 'objectFit' | 'onChange' | 'src'
>
export default function ImageDisplay({
  alt = '',
  asImg = false,
  exitZoom,
  id,
  inlineStyle = false,
  isLoading = false,
  maxWidth,
  objectFit = ObjectFits.Contain,
  padding = 'none',
  setIsLoading,
  size,
  url,
  withLink = false,
  withLoader = true,
  zoomed = false,
  ...props
}: Props) {
  let $t: Translator | null = null
  // When using this component in the BlockNote editor it randomly happens that
  // React complains about the NextIntl context not being found, though the
  // translation is working fine.
  // To avoid the error in the console, simply silently swallow it.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    $t = useTranslations()
  } catch {
    // Nothing to do here.
  }

  const assetsURLs = useUrls()

  let isUrlValid = true
  try {
    // eslint-disable-next-line no-new
    new URL(url.startsWith('/') ? `${window.location.origin}/${url}` : url)
  } catch (error) {
    console.error(error)

    isUrlValid = false
  }

  const handleImageLoaded = (event: SyntheticEvent<HTMLImageElement>) => {
    if (isFunction(props.onLoad)) {
      props.onLoad(event)
    }

    setIsLoading?.(false)
  }

  const handleClick = () => {
    if (!zoomed || !isFunction(exitZoom)) {
      return
    }

    exitZoom()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!zoomed || !isFunction(exitZoom)) {
      return
    }

    if (event.key === 'Escape' || event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()

      exitZoom()
    }
  }

  useOnPageKeyDown(
    (event) => {
      if (!zoomed || !isFunction(exitZoom) || event.defaultPrevented) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      exitZoom()
    },
    ['Escape', ' ', 'Enter'],
    { bodyOnly: false },
    [exitZoom, zoomed],
  )

  const zoomable = !asImg && isFunction(exitZoom)

  const pxHeight = asPx(props.height ?? size?.height) as number
  const pxWidth = asPx(props.width ?? size?.width) as number

  const content = (
    <>
      {isUrlValid ? (
        <>
          {asImg || inlineStyle ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt={alt || ''}
                className={
                  inlineStyle
                    ? undefined
                    : cn(
                        'absolute size-full inset-0 text-transparent rounded-[7px] max-w-full',
                        padding === 'small' ? 'p-1' : '',
                        props.className,
                      )
                }
                height={isNumber(pxHeight) ? pxHeight - 2 : undefined}
                sizes={props.sizes}
                src={url}
                style={{
                  objectFit,
                  ...(inlineStyle
                    ? {
                        borderRadius: SizeRadiusS,
                        display: 'block',
                        height: isNumber(pxHeight)
                          ? undefined
                          : (props.height ?? size?.height ?? undefined),
                        maxWidth:
                          isNumber(maxWidth) || !isEmpty(maxWidth)
                            ? isNumber(maxWidth)
                              ? `${maxWidth}px`
                              : maxWidth
                            : '100%',
                        padding:
                          padding === 'small'
                            ? asPx(SizeSpacing1, { withUnit: true })
                            : '',
                        width: isNumber(pxWidth)
                          ? undefined
                          : isEmpty(props.width ?? size?.width)
                            ? '100%'
                            : undefined,
                      }
                    : {}),
                  ...props.style,
                }}
                title={alt}
                width={isNumber(pxWidth) ? pxWidth : undefined}
                onLoad={asImg || inlineStyle ? undefined : handleImageLoaded}
              />
            </>
          ) : (
            <Image
              alt={alt || ''}
              className={cn(
                zoomed ? '' : 'rounded-[7px] max-w-full h-auto',
                padding === 'small' ? 'p-1' : '',
                props.className,
              )}
              fill={
                (props.fill ??
                  (!isNumber(props.width) && !isNumber(props.height))) ||
                !isNumber(props.width) ||
                !isNumber(props.height)
              }
              sizes={
                props.sizes ||
                ((isNumber(maxWidth) || !isEmpty(maxWidth)) &&
                  (isNumber(maxWidth) ? `${maxWidth}px` : maxWidth)) ||
                undefined
              }
              src={url}
              style={{
                maxHeight: isNumber(props.height)
                  ? `${props.height - 2}px`
                  : undefined,
                maxWidth: isNumber(props.width)
                  ? `min(100%, ${props.width}px)`
                  : undefined,
                objectFit,
                ...props.style,
              }}
              title={alt}
              onLoad={handleImageLoaded}
              {...omit(
                ['alt', 'className', 'fill', 'onLoad', 'sizes', 'src', 'style'],
                props,
              )}
            />
          )}
        </>
      ) : (
        !isUrlValid && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt=""
            className={
              inlineStyle
                ? undefined
                : cj('h-20 block', padding === 'small' ? 'p-1' : '')
            }
            src={
              isUrlValid ? url : `${assetsURLs.assets}/images/OopsSnowie.png`
            }
            style={
              inlineStyle
                ? {
                    display: 'block',
                    height: asPx(SizeSpacing20, { withUnit: true }),
                    padding:
                      padding === 'small'
                        ? asPx(SizeSpacing1, { withUnit: true })
                        : undefined,
                  }
                : {}
            }
          />
        )
      )}

      {withLoader && isLoading && (
        <div className="absolute inset-0 flex justify-center items-center bg-secondary opacity-50 rounded-[7px]">
          <Loader />
        </div>
      )}

      {!isUrlValid && $t !== null && (
        <div className="flex items-center justify-center rounded-[7px]">
          <DisplayError display message={$t?.('image.error.loading')} />
        </div>
      )}
    </>
  )

  const imageWrapper = (
    <div
      id={id}
      className={
        inlineStyle
          ? undefined
          : cj(
              'size-full',
              zoomed
                ? 'fixed left-0 top-0 cursor-zoom-out bg-[rgba(0,0,0,0.5)]'
                : 'rounded-sm',
            )
      }
      role={zoomable ? 'button' : undefined}
      style={
        inlineStyle
          ? {
              borderRadius: SizeRadiusS,
              height: objectFit === ObjectFits.Cover ? '100%' : undefined,
              maxWidth:
                isNumber(maxWidth) || !isEmpty(maxWidth)
                  ? isNumber(maxWidth)
                    ? `${maxWidth}px`
                    : maxWidth
                  : '100%',
              width: '100%',
            }
          : undefined
      }
      tabIndex={zoomable ? -1 : undefined}
      onClick={zoomable ? handleClick : undefined}
      onKeyDown={zoomable ? handleKeyDown : undefined}
    >
      {inlineStyle ? (
        <LegacyTable
          col={!isUrlValid}
          gap={spacings[1]}
          style={{
            height: '100%',
            maxWidth: '100%',
            width: '100%',
          }}
        >
          {withLink ? (
            <a
              href={url}
              rel="noopener noreferrer"
              style={{ display: 'block', maxWidth: '100%', width: '100%' }}
              target="_blank"
            >
              {content}
            </a>
          ) : (
            content
          )}
        </LegacyTable>
      ) : (
        <div
          className={
            inlineStyle
              ? undefined
              : cj(
                  'relative p-0 m-0 flex items-center justify-center',
                  zoomed
                    ? 'm-3 size-[calc(100%-theme(spacing.6))]'
                    : 'size-full rounded-[7px]',
                  isUrlValid
                    ? ''
                    : 'flex flex-col items-center justify-center gap-1 py-1 pb-3',
                )
          }
        >
          {withLink ? (
            <a href={url} rel="noopener noreferrer" target="_blank">
              {content}
            </a>
          ) : (
            content
          )}
        </div>
      )}
    </div>
  )

  return zoomed ? createPortal(imageWrapper, document.body) : imageWrapper
}
